const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** The sidebar tile counts down, and the trial card switches copy, for the last 7 days. */
export const CLINICIAN_TRIAL_END_COUNTDOWN_DAYS = 7;

export type ClinicianTrialEndPhase = 'more_than_7' | 'last_7' | 'ended';

export type ClinicianTrialEndState = {
  phase: ClinicianTrialEndPhase;
  /** Whole days left, rounded up; 0 once the trial has ended. */
  daysLeft: number;
  /** When the trial ends or ended. */
  trialEnd: string;
};

export type ClinicianTrialEndInput = {
  isEnabled: boolean;
  isHeidi2: boolean;
  isAppleCompliantBuild: boolean;
  isManaged: boolean;
  isClinicianTrialUser: boolean;
  clinicianTrialEnded: boolean;
  /** The user has used a Clinician trial before, so a lapsed end date is theirs. */
  hadClinicianTrial: boolean;
  isFreeTier: boolean;
  trialEnd: string | null | undefined;
};

const daysUntil = (trialEnd: string, now: Date) =>
  Math.ceil((Date.parse(trialEnd) - now.getTime()) / MS_PER_DAY);

/** Where a Heidi II member is in their Clinician trial, or `null` when the flow does not apply. */
export function deriveClinicianTrialEndState(
  input: ClinicianTrialEndInput,
  now: Date
): ClinicianTrialEndState | null {
  const { trialEnd } = input;
  if (
    !input.isEnabled ||
    !input.isHeidi2 ||
    input.isAppleCompliantBuild ||
    input.isManaged ||
    trialEnd == null ||
    Number.isNaN(Date.parse(trialEnd))
  ) {
    return null;
  }

  const daysLeft = daysUntil(trialEnd, now);
  if (input.isClinicianTrialUser && daysLeft > 0) {
    return {
      phase: daysLeft <= CLINICIAN_TRIAL_END_COUNTDOWN_DAYS ? 'last_7' : 'more_than_7',
      daysLeft,
      trialEnd,
    };
  }

  const hasEnded =
    !input.isClinicianTrialUser &&
    input.isFreeTier &&
    daysLeft <= 0 &&
    (input.clinicianTrialEnded || input.hadClinicianTrial);
  return hasEnded ? { phase: 'ended', daysLeft: 0, trialEnd } : null;
}

/** The state a forced trial end date produces, for the dev-tools override. */
export function deriveForcedClinicianTrialEndState(
  trialEnd: string,
  now: Date
): ClinicianTrialEndState {
  const daysLeft = daysUntil(trialEnd, now);
  if (daysLeft <= 0) return { phase: 'ended', daysLeft: 0, trialEnd };
  return {
    phase: daysLeft <= CLINICIAN_TRIAL_END_COUNTDOWN_DAYS ? 'last_7' : 'more_than_7',
    daysLeft,
    trialEnd,
  };
}
